// src/pages/Users/Galleries/AlbumGaleriePage.jsx

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FaImages } from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";

import { useUser } from "../../Home/UserContext";
import { getRoleFromRoleId } from "../../../utils/roleHelper";

import useScrollToTop from "../../../hooks/useScollToTop";
import ScrollIndicator from "../../Home/ScrollIndicator";
import HeaderTitle from "../../../components/HeaderTitle/HeaderTitle";
import DashboardNavigation from "../../DashboardNavigation/DashboardNavigation";
import Reveal from "./../../../components/Reveal/Reveal";

import { getGalleryById } from "../../../services/gallerieServices";
import {
  movePhoto as apiMovePhoto,
  togglePhotoCover,
} from "../../../services/photoServices";

import AddPhotoToAlbumModal from "./AddPhotoToAlbumModal";
import RemovePhotoFromAlbumModal from "./RemovePhotoFromAlbumModal";

import AlbumActionsBar from "./components/AlbumActionsBar";
import AlbumDetailHero from "./components/AlbumDetailHero";
import AlbumDetailSkeleton from "./components/AlbumDetailSkeleton";
import AlbumLightbox from "./components/AlbumLightbox";
import AlbumPhotoGrid from "./components/AlbumPhotoGrid";
import EmptyState from "./components/EmptyState";

import { getOrderedPhotos } from "./utils/galleriesUtils";

const PAGE_SIZE = 24;

export default function AlbumGaleriePage() {
  useScrollToTop();

  const { id } = useParams();
  const navigate = useNavigate();

  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);
  const isManager = userRole === "admin" || userRole === "enseignant";

  const [gallery, setGallery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [perPage, setPerPage] = useState(PAGE_SIZE);

  const [addPhotoOpen, setAddPhotoOpen] = useState(false);
  const [removePhotoOpen, setRemovePhotoOpen] = useState(false);

  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [moving, setMoving] = useState(false);

  const fetchGallery = useCallback(async () => {
    if (!id) return;

    setLoading(true);
    setError("");

    try {
      const response = await getGalleryById(Number(id));

      if (response && response.id) {
        setGallery(response);
      } else {
        setGallery(null);
        setError("Album introuvable.");
      }
    } catch (err) {
      console.error("Erreur chargement album :", err);
      setGallery(null);
      setError("Impossible de charger cet album pour le moment.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchGallery();
  }, [fetchGallery]);

  const photos = useMemo(() => getOrderedPhotos(gallery), [gallery]);

  const visiblePhotos = useMemo(
    () => photos.slice(0, perPage),
    [photos, perPage]
  );

  const hasMore = visiblePhotos.length < photos.length;

  const openLightbox = useCallback((index) => {
    setLightboxIndex(index);
  }, []);

  const closeLightbox = useCallback(() => {
    setLightboxIndex(null);
  }, []);

  const copyShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success("Lien de l’album copié.");
    } catch {
      toast.error("Impossible de copier le lien.");
    }
  };

  const handleToggleCover = async (photo) => {
    if (!photo?.id) return;

    try {
      setGallery((current) => {
        if (!current) return current;

        const nextPhotos = Array.isArray(current.photos)
          ? current.photos.map((item) => ({
              ...item,
              is_cover: item.id === photo.id ? !photo.is_cover : false,
            }))
          : [];

        return {
          ...current,
          photos: nextPhotos,
        };
      });

      await togglePhotoCover(photo.id, { gallerieId: id });
      await fetchGallery();

      toast.success(
        photo.is_cover
          ? "Photo retirée de la couverture."
          : "Photo définie comme couverture."
      );
    } catch (err) {
      console.error(err);
      toast.error("Impossible de modifier la couverture.");
      fetchGallery();
    }
  };

  const handleMovePhoto = async (fromIndex, toIndex) => {
    if (!isManager || moving) return;
    if (fromIndex === toIndex) return;
    if (toIndex < 0 || toIndex >= photos.length) return;

    const movedPhoto = visiblePhotos[fromIndex];
    if (!movedPhoto?.id) return;

    setMoving(true);

    try {
      setGallery((current) => {
        if (!current || !Array.isArray(current.photos)) return current;

        const ordered = getOrderedPhotos(current);
        const copy = [...ordered];
        const [item] = copy.splice(fromIndex, 1);
        copy.splice(toIndex, 0, item);

        return {
          ...current,
          photos: copy,
        };
      });

      await apiMovePhoto(movedPhoto.id, toIndex, { gallerieId: id });
      await fetchGallery();
    } catch (err) {
      console.error(err);
      toast.error("Échec du déplacement de la photo.");
      fetchGallery();
    } finally {
      setMoving(false);
    }
  };

  const handleModalClose = async () => {
    setAddPhotoOpen(false);
    setRemovePhotoOpen(false);
    await fetchGallery();
  };

  if (!loading && error && !gallery) {
    return (
      <section className="min-h-screen bg-slate-50 text-slate-950 dark:bg-slate-950 dark:text-slate-50">
        <ScrollIndicator />
        <div className="pt-[96px] sm:pt-[120px] lg:pt-[160px]" aria-hidden />

        <main className="mx-auto w-full max-w-7xl px-3 pb-24 sm:px-6 lg:px-8">
          <EmptyState
            title="Album introuvable"
            description={error}
            onReset={() => navigate("/galerie-photos")}
            resetLabel="Retour aux galeries"
          />
        </main>
      </section>
    );
  }

  return (
    <section className="min-h-screen bg-slate-50 text-slate-950 dark:bg-slate-950 dark:text-slate-50">
      <ScrollIndicator />

      <div className="pt-[96px] sm:pt-[120px] lg:pt-[160px]" aria-hidden />

      <main className="mx-auto w-full max-w-7xl px-3 pb-28 sm:px-6 lg:px-8">
        <HeaderTitle
          role="Utilisateur"
          title="Album photo"
          subtitle="Consulte les photos de l’album, ouvre le diaporama et retrouve les souvenirs du club."
          icon={FaImages}
          align="left"
          className="mb-4"
        />

        {/* <div className="mb-4">
          <DashboardNavigation role={userRole} />
        </div> */}

        {loading && <AlbumDetailSkeleton />}

        {!loading && gallery && (
          <>
            <Reveal>
              <AlbumDetailHero gallery={gallery} photos={photos} />
            </Reveal>

            <div className="sticky top-[76px] z-30 -mx-3 mt-4 border-y border-slate-200/80 bg-slate-50/95 px-3 py-3 backdrop-blur-xl dark:border-slate-800 dark:bg-slate-950/90 sm:static sm:mx-0 sm:border-0 sm:bg-transparent sm:px-0 sm:py-0 sm:backdrop-blur-0">
              <AlbumActionsBar
                gallery={gallery}
                photosCount={photos.length}
                isManager={isManager}
                onBack={() => navigate("/galerie-photos")}
                onShare={copyShare}
                onAddPhoto={() => setAddPhotoOpen(true)}
                onRemovePhoto={() => setRemovePhotoOpen(true)}
              />
            </div>

            <section className="mt-5">
              {photos.length === 0 ? (
                <EmptyState
                  title="Aucune photo"
                  description="Cet album ne contient pas encore de photo."
                  onReset={
                    isManager ? () => setAddPhotoOpen(true) : undefined
                  }
                  resetLabel="Ajouter une photo"
                />
              ) : (
                <>
                  <AlbumPhotoGrid
                    photos={visiblePhotos}
                    fullPhotos={photos}
                    isManager={isManager}
                    moving={moving}
                    onOpen={openLightbox}
                    onToggleCover={handleToggleCover}
                    onMovePhoto={handleMovePhoto}
                  />

                  {hasMore && (
                    <div className="mt-6 flex justify-center">
                      <button
                        type="button"
                        onClick={() => setPerPage((prev) => prev + PAGE_SIZE)}
                        className="inline-flex min-h-12 items-center justify-center rounded-2xl bg-slate-950 px-6 text-sm font-bold text-white transition active:scale-95 dark:bg-white dark:text-slate-950"
                      >
                        Charger plus de photos
                      </button>
                    </div>
                  )}
                </>
              )}
            </section>
          </>
        )}
      </main>

      <AlbumLightbox
        isOpen={lightboxIndex !== null}
        photos={photos}
        startIndex={lightboxIndex || 0}
        onClose={closeLightbox}
      />

      {addPhotoOpen && (
        <AddPhotoToAlbumModal
          isOpen={addPhotoOpen}
          onClose={handleModalClose}
          galleryId={id}
        />
      )}

      {removePhotoOpen && (
        <RemovePhotoFromAlbumModal
          isOpen={removePhotoOpen}
          onClose={handleModalClose}
          galleryId={id}
        />
      )}

      <ToastContainer />
    </section>
  );
}